import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';

export interface IPromptAuditEvent {
  id: number;
  action: string;
  actorId: number | null;
  metadata: Record<string, any>;
  createdAt: string;
}

export interface IPromptCatalogEntry {
  promptKey: string;
  kind: string;
  /** null = Global/Default scope; non-null = module-specific override */
  module: string | null;
  category: string | null;
  latestVersion: string;
  latestLifecycle: string;
  activeVersionId: number | null;
  activeVersion: string | null;
  activeContentDigest: string | null;
  activeUpdatedAt: string | null;
  activatedAt: string | null;
}

/** Flat list — one entry per (promptKey, module) scope that exists in DB */
export type ICatalogResponse = IPromptCatalogEntry[];

export interface IPromptVersion {
  id: number;
  promptKey: string;
  kind: string;
  version: string;
  lifecycle: string;
  module: string | null;
  category: string | null;
  content: Record<string, string>;
  contentDigest: string;
  requiredCapabilities: string[];
  requiredRoles: string[];
  notes: string | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  auditTrail: IPromptAuditEvent[];
}

export interface ICreatePromptVersionDto {
  promptKey: string;
  kind: string;
  module: string | null;
  category: string | null;
  content: Record<string, string>;
  requiredCapabilities: string[];
  requiredRoles: string[];
  notes?: string;
  bumpType: 'major' | 'minor' | 'patch';
}

export interface IPromptMetadata {
  capabilities: string[];
  roles: string[];
  modules: string[];
  categories: string[];
}

export const aiPromptsApi = createApi({
  reducerPath: 'aiPromptsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AiPrompts'],
  endpoints: (build) => ({
    getCatalog: build.query<IPromptCatalogEntry[], void>({
      query: () => ({ url: '/system/ai-prompt/catalog', method: 'GET' }),
      transformResponse: (response: { data: IPromptCatalogEntry[] }) => response.data,
      providesTags: ['AiPrompts'],
    }),

    listVersions: build.query<IPromptVersion[], { promptKey: string; module?: string | null }>({
      query: ({ promptKey, module }) => ({
        url: '/system/ai-prompt/versions',
        method: 'GET',
        params: { promptKey, module: module === null ? 'null' : module },
      }),
      transformResponse: (response: { data: IPromptVersion[] }) => response.data,
      providesTags: ['AiPrompts'],
    }),

    getDefaultContent: build.query<{ content: Record<string, string> } | null, string>({
      query: (promptKey) => ({
        url: '/system/ai-prompt/version/0/default-content',
        method: 'GET',
        params: { promptKey },
      }),
      transformResponse: (response: { data: { content: Record<string, string> } | null }) => response.data,
    }),

    createVersion: build.mutation<IPromptVersion, ICreatePromptVersionDto>({
      query: (body) => ({ url: '/system/ai-prompt/version', method: 'POST', body }),
      invalidatesTags: ['AiPrompts'],
    }),

    activateVersion: build.mutation<IPromptVersion, { id: number; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/system/ai-prompt/version/${id}/activate`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['AiPrompts'],
    }),

    deprecateVersion: build.mutation<IPromptVersion, { id: number; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/system/ai-prompt/version/${id}/deprecate`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['AiPrompts'],
    }),

    hideVersion: build.mutation<IPromptVersion, { id: number; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/system/ai-prompt/version/${id}/hide`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['AiPrompts'],
    }),

    restoreVersion: build.mutation<IPromptVersion, { id: number; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/system/ai-prompt/version/${id}/restore`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['AiPrompts'],
    }),

    resetOverride: build.mutation<IPromptVersion, number>({
      query: (id) => ({
        url: `/system/ai-prompt/version/${id}/reset-override`,
        method: 'POST',
      }),
      invalidatesTags: ['AiPrompts'],
    }),

    deleteScope: build.mutation<{ deleted: number }, { promptKey: string; module?: string | null }>({
      query: ({ promptKey, module }) => ({
        url: '/system/ai-prompt/scope',
        method: 'DELETE',
        params: { promptKey, module: module === null ? 'null' : (module ?? 'null') },
      }),
      invalidatesTags: ['AiPrompts'],
    }),

    getMetadata: build.query<IPromptMetadata, void>({
      query: () => ({ url: '/system/ai-prompt/metadata', method: 'GET' }),
      transformResponse: (response: { data: IPromptMetadata }) => response.data,
    }),
  }),
});

export const {
  useGetCatalogQuery,
  useListVersionsQuery,
  useGetDefaultContentQuery,
  useCreateVersionMutation,
  useActivateVersionMutation,
  useDeprecateVersionMutation,
  useHideVersionMutation,
  useRestoreVersionMutation,
  useResetOverrideMutation,
  useDeleteScopeMutation,
  useGetMetadataQuery,
} = aiPromptsApi;
